import React from 'react';
import { Alert, Button, Divider, List } from '@mui/material';
import { CircularProgress } from '@material-ui/core';

import { Employee } from 'containers/CommunityPage/EmployeeSearch/UserMultiSelect';

const listItemsWithDividers = (listItems: JSX.Element[]): JSX.Element[] =>
  listItems.flatMap((item, index) =>
    index < listItems.length - 1 ? [item, <Divider key={`divider${index}`} />] : [item]
  );

export type UserListProps = {
  users: Employee[];
  loading: boolean;
  total: number;
  selectedUsers: Employee[];
  setSelectedUsers: (v: Employee[]) => void;
  fetchMore: () => void;
  renderEmployee: (renderEmployeeProps: {
    user: Employee;
    isSelected: boolean;
    onClick: () => void;
  }) => JSX.Element;
};

export const UserList = ({
  loading,
  selectedUsers,
  setSelectedUsers,
  fetchMore,
  users,
  total,
  renderEmployee,
}: UserListProps): JSX.Element | null => {
  if (users.length === 0) {
    return <Alert severity="warning">No matching results</Alert>;
  }

  const selectedIds = new Set(selectedUsers.map((user) => user.id));
  const pagination = total === undefined ? users.length : `${users.length}/${total}`;
  return (
    <List style={{ overflow: 'auto', maxHeight: 260, fontSize: 13 }}>
      {listItemsWithDividers(
        users
          .map((user) => {
            const isSelected = selectedIds.has(user.id);
            if (isSelected) {
              return null;
            }
            return renderEmployee({
              user,
              isSelected: selectedIds.has(user.id),
              onClick: () =>
                setSelectedUsers(
                  isSelected
                    ? selectedUsers.filter(({ id }) => id !== user.id)
                    : [user, ...selectedUsers]
                ),
            });
          })
          .filter(Boolean)
      )}
      {users.length < total && (
        <Button
          sx={{ mt: 1 }}
          startIcon={loading && <CircularProgress size="1rem" />}
          disabled={loading || users.length === total}
          onClick={fetchMore}
        >
          <>Fetch more {pagination}</>
        </Button>
      )}
    </List>
  );
};
